import { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useLocationControl } from '../services/useLocationControl'
import { buildLocationControlColumns } from '../configs/locationControl.columns'
import { locationControlFormFields } from '../configs/locationControl.form'
import { buildLocationCreatePayload, buildLocationUpdatePayload } from '../helpers/locationPayload'
import LocationForm from './LocationForm'
import LocationUploadPanel from './LocationUploadPanel'
import { AppModal } from '../../../../../../generic/ui/Modal'
import ConfirmDialog from '../../../../../../generic/ui/ConfirmDialog/ConfirmDialog'
import { GridTable, TableToolbar } from '../../../../../../generic/grid-table'
import useTableEditing from '../../../../../../generic/universal-table/hooks/useTableEditing'
import { FormFieldDefinition } from '../../../../../../generic/universal-table/types/form.types'
import { IWarehouseLocation } from '../../../../../../modules/API/Warehouse/models/locations'

const UPLOAD_ACCEPT = ['.xls', '.xlsx', '.csv']

const LocationControl = () => {
  const {
    rows,
    totalCount,
    isLoading,
    error,
    searchQuery,
    handleSearch,
    isUploadOpen,
    toggleUploadModal,
    handleUploadFile,
    resolveWarehouseLabel,
    resolveRoomLabel,
    warehouseOptions,
    roomOptions,
    warehouseList,
    createLocation,
    updateLocation,
    removeLocation,
  } = useLocationControl()

  const {
    isFormOpen,
    editingMode,
    selectedRow,
    handleOpenCreate,
    handleOpenEdit,
    handleCloseForm,
    isConfirmDeleteOpen,
    isDeleting,
    handleRequestDelete,
    handleCancelDelete,
    handleConfirmDelete,
  } = useTableEditing<IWarehouseLocation>()

  const columns = useMemo(
    () => buildLocationControlColumns(resolveWarehouseLabel, resolveRoomLabel),
    [resolveWarehouseLabel, resolveRoomLabel],
  )

  // Active is hidden in create mode — the server creates the row active by default.
  const formFields = useMemo<FormFieldDefinition<IWarehouseLocation>[]>(
    () =>
      locationControlFormFields
        .filter((field) => editingMode === 'edit' || field.name !== 'Active')
        .map((field) => {
          if (field.name === 'WarehouseID') return { ...field, getOptions: () => warehouseOptions }
          if (field.name === 'Room') return { ...field, getOptions: () => roomOptions }
          return field
        }),
    [editingMode, warehouseOptions, roomOptions],
  )

  const [isSaving, setIsSaving] = useState<boolean>(false)

  const handleFormSubmit = useCallback(
    async (values: Partial<IWarehouseLocation>): Promise<void> => {
      setIsSaving(true)
      try {
        if (editingMode === 'edit' && selectedRow) {
          await updateLocation(
            buildLocationUpdatePayload(selectedRow.RecID, { ...selectedRow, ...values }),
          )
        } else {
          await createLocation(buildLocationCreatePayload(values))
        }
        handleCloseForm()
      } finally {
        setIsSaving(false)
      }
    },
    [editingMode, selectedRow, createLocation, updateLocation, handleCloseForm],
  )

  const formTitle = editingMode === 'edit' ? 'עריכת איתור' : 'הוספת איתור חדש'

  return (
    <TableSection>
      {isUploadOpen && (
        <AppModal
          title='ייבוא איתורים מקובץ'
          Children={LocationUploadPanel}
          controlFunc={toggleUploadModal}
          childrenProps={{
            accept: UPLOAD_ACCEPT,
            warehouses: warehouseList ?? [],
            onUpload: handleUploadFile,
          }}
          size='md'
        />
      )}

      {isFormOpen && (
        <AppModal
          title={formTitle}
          controlFunc={handleCloseForm}
          Children={LocationForm}
          childrenProps={{
            fields: formFields,
            initialValues: selectedRow ?? undefined,
            onSubmit: handleFormSubmit,
            onDelete: editingMode === 'edit' ? handleRequestDelete : undefined,
            isLoading: isSaving,
            isDeleting,
            submitLabel: editingMode === 'edit' ? 'עדכן' : 'הוסף',
          }}
          size='md'
        />
      )}

      <ConfirmDialog
        open={isConfirmDeleteOpen}
        title='מחיקת איתור'
        message='האם למחוק את האיתור? פעולה זו אינה הפיכה.'
        isLoading={isDeleting}
        onCancel={handleCancelDelete}
        onConfirm={() => handleConfirmDelete((row) => removeLocation(row.RecID))}
      />

      {error && <ErrorMessage role='alert'>{error}</ErrorMessage>}
      <GridTable<IWarehouseLocation>
        title='בקרת איתורים'
        count={totalCount}
        columns={columns}
        rows={rows}
        getRowId={(row) => row.RecID}
        isLoading={isLoading}
        onRowClick={handleOpenEdit}
        emptyTitle='לא נמצאו איתורים'
        toolbarActions={
          <TableToolbar
            searchValue={searchQuery}
            onSearchChange={handleSearch}
            searchPlaceholder='חיפוש איתור…'
            onImport={toggleUploadModal}
            importLabel='ייבוא איתורים מאקסל'
            onAdd={handleOpenCreate}
            addLabel='הוסף איתור'
          />
        }
      />
    </TableSection>
  )
}

export default LocationControl

const TableSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8em;
  width: 100%;
  height: 100%;
  padding: 0.5em;
  overflow: hidden;
`

const ErrorMessage = styled.p`
  margin: 0;
  direction: rtl;
  text-align: start;
  font-size: ${({ theme }) => theme.text.md};
  color: ${({ theme }) => theme.colors.danger};
`
